import React, { ReactNode, useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Text, TextButton } from '@wix/design-system';
import { HELP_LINKS } from '../../../../constants/HelpLinks';
import { ModalName } from '../../../../constants/ModalName';
import { useFormattedBytes } from '../../useFormattedBytes';
import { useModal, useModalHandlersAndOptions } from '../../useModal';
import { useStorageQuotaBI } from '../useStorageQuotaBI';
import Illustration from '../../../../assets/illustrations/fullStorageIllustration.svg';
import { Theme } from '../../useModal/useModal.types';
import { StorageQuotaUseCase } from '../useStorageQuotaAwareHandler/StorageQuotaUseCase';
import { useStorageQuota } from '../useStorageQuota';

export const useStorageFullModal = (): ReactNode => {
  const useCase: StorageQuotaUseCase =
    useModalHandlersAndOptions(ModalName.StorageFull).additionalOptions.useCase ?? StorageQuotaUseCase.UPLOAD;
  const { formatMessage } = useIntl();

  const { storageQuotaData } = useStorageQuota();
  const hasQuotaData = !!storageQuotaData;
  const upgradeUrl = storageQuotaData?.upgradeUrl;

  const { trackLearnMoreClick, trackUpgradeButtonClick, trackStorageFullPopupOpened, trackStorageFullPopupClosed } =
    useStorageQuotaBI(storageQuotaData);
  const handleLearnMoreClick = (): void => {
    trackLearnMoreClick({
      clickOrigin: 'storage_is_full_popup',
    });
  };
  const onUpgradeClick = (): void => {
    trackUpgradeButtonClick({
      upgradeOrigin: 'storage_is_full_popup',
    });
  };

  const confirmButtonProps = useMemo(
    () => (upgradeUrl ? { as: 'a' as const, href: upgradeUrl, target: '_blank' } : undefined),
    [upgradeUrl],
  );

  const quotaFormatted = useFormattedBytes(storageQuotaData?.quota.storage);

  const descriptions: Record<StorageQuotaUseCase, () => ReactNode> = {
    [StorageQuotaUseCase.COPY]: () =>
      upgradeUrl ? (
        <FormattedMessage id="Storage.Media_Modal_Storage_Full_Copy_Description" values={{ total: quotaFormatted }} />
      ) : (
        <FormattedMessage
          id="Storage.Media_Modal_Storage_Full_Copy_NoUpgrade_Description"
          values={{ total: quotaFormatted }}
        />
      ),
    [StorageQuotaUseCase.UPLOAD]: () =>
      upgradeUrl ? (
        <FormattedMessage
          id="Storage.Media_Modal_Storage_Full_General_Description"
          values={{ total: quotaFormatted }}
        />
      ) : (
        <FormattedMessage
          id="Storage.Media_Modal_Storage_Full_General_NoUpgrade_Description"
          values={{ total: quotaFormatted }}
        />
      ),
    [StorageQuotaUseCase.VIDEO_MAKER]: () =>
      upgradeUrl ? (
        <FormattedMessage
          id="Storage.Media_Video_Maker_Modal_Storage_Full_Description"
          values={{ total: quotaFormatted }}
        />
      ) : (
        <FormattedMessage
          id="Storage.Media_Video_Maker_Modal_Storage_Full_NoUpgrade_Description"
          values={{ total: quotaFormatted }}
        />
      ),
    [StorageQuotaUseCase.MEDIA_STUDIO]: () =>
      upgradeUrl ? (
        <FormattedMessage
          id="Storage.Media_Photo_Studio_Modal_Storage_Full_Description"
          values={{ total: quotaFormatted }}
        />
      ) : (
        <FormattedMessage
          id="Storage.Media_Photo_Studio_Modal_Storage_Full_NoUpgrade_Description"
          values={{ total: quotaFormatted }}
        />
      ),
    [StorageQuotaUseCase.ADOBE_EXPRESS]: () =>
      upgradeUrl ? (
        <FormattedMessage
          id="Storage.Media_Adobe_Express_Modal_Storage_Full_Description"
          values={{ total: quotaFormatted }}
        />
      ) : (
        <FormattedMessage
          id="Storage.Media_Adobe_Express_Modal_Storage_Full_NoUpgrade_Description"
          values={{ total: quotaFormatted }}
        />
      ),
    [StorageQuotaUseCase.SHUTTERSTOCK_PURCHASE]: () =>
      upgradeUrl ? (
        <FormattedMessage
          id="Storage.Media_Modal_Storage_Full_Not_Added_Description"
          values={{ total: quotaFormatted }}
        />
      ) : (
        <FormattedMessage
          id="Storage.Media_Modal_Storage_Full_NoUpgrade_Not_Added_Description"
          values={{ total: quotaFormatted }}
        />
      ),
  };
  const fallbackDescription = <FormattedMessage id="Storage.Media_Modal_Storage_Full_Not_Added_No_Num_Description" />;

  const onClose = useCallback(() => {
    trackStorageFullPopupClosed(useCase);
  }, [useCase, trackStorageFullPopupClosed]);
  const onOpen = useCallback(() => {
    trackStorageFullPopupOpened(useCase);
  }, [useCase, trackStorageFullPopupOpened]);

  return useModal(ModalName.StorageFull, {
    title: formatMessage({ id: 'Storage.Media_Modal_Storage_Full_Title' }),
    confirmText: formatMessage({
      id: upgradeUrl ? 'Storage.Media_Modal_Storage_Full_Upgrade_Button' : 'Storage.Media_Modal_Storage_Full_Ok_Button',
    }),
    confirmButtonProps: upgradeUrl ? confirmButtonProps : undefined,
    cancelText: upgradeUrl ? formatMessage({ id: 'Storage.Media_Modal_Storage_Full_Not_Now_Button' }) : undefined,
    onOk: upgradeUrl ? onUpgradeClick : onClose,
    onOpen,
    onClose,
    onCancel: upgradeUrl ? onClose : undefined,
    theme: upgradeUrl ? Theme.Premium : Theme.Standard,
    image: <Illustration />,
    content: (
      <Text>
        {hasQuotaData ? descriptions[useCase]() : fallbackDescription}{' '}
        <TextButton
          as="a"
          underline="always"
          onClick={handleLearnMoreClick}
          target="_blank"
          href={HELP_LINKS.LEARN_MORE_STORAGE_MEDIA}
          dataHook="learn-more-storage-media"
        >
          <FormattedMessage id="Storage.Media_Toast_Learn_More_Link" />
        </TextButton>
      </Text>
    ),
  });
};
